import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wrench } from "@phosphor-icons/react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBob21lJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODU4NzIzMTF8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_pro-connect-315/artifacts/ws4b3em5_1.png";

const AuthPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get("role"); // "client" | "contractor" | null

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regEmailConfirm, setRegEmailConfirm] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regReferrer, setRegReferrer] = useState(searchParams.get("ref") || "");
  const [submitting, setSubmitting] = useState(false);

  // Password must be at least 8 characters and contain at least one letter and one number
  const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

  const goNext = async (user) => {
    // If they came from the role picker with ?role=..., apply it now
    if (
      preselectedRole &&
      !user.role &&
      (preselectedRole === "client" || preselectedRole === "contractor")
    ) {
      try {
        await api.post("/users/role", { role: preselectedRole });
        user = { ...user, role: preselectedRole };
      } catch (e) {
        // if role apply fails we still fall through and route them to /role
      }
    }

    if (!user.role) navigate("/role");
    else if (user.role === "contractor" && !user.contractor_profile)
      navigate("/contractor/register");
    else if (user.role === "client") navigate("/client/categories");
    else navigate("/dashboard");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(loginEmail, loginPassword);
      toast.success("Welcome back!");
      goNext(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regEmail.trim().toLowerCase() !== regEmailConfirm.trim().toLowerCase()) {
      toast.error("Emails do not match. Please re-enter to confirm.");
      return;
    }
    if (!PASSWORD_RULE.test(regPassword)) {
      toast.error("Password must be at least 8 characters and include a letter and a number.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await register(regName, regEmail, regPassword, regReferrer.trim() || null);
      toast.success(regReferrer.trim() ? "Account created — welcome via referral!" : "Account created!");
      goNext(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FFFFFF]" data-testid="auth-page">
      <Toaster position="top-right" richColors />
      <div className="grid min-h-screen md:grid-cols-2">
        {/* Left: Photo */}
        <div className="relative hidden md:block" data-testid="auth-hero-visual">
          <img
            src={AUTH_BG}
            alt="Modern construction home"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A0F0D]/40" />
          <div className="relative z-10 flex h-full flex-col justify-end p-12 text-[#FFFFFF]">
            <div className="max-w-md">
              <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                Local pros.
                <br />
                Real work.
                <br />
                <span className="text-[#C65D47]">Done right.</span>
              </h1>
              <p className="mt-6 text-base leading-relaxed text-[#FFFFFF]/85">
                The trusted marketplace where homeowners meet vetted contractors —
                electricians, plumbers, gardeners and handymen — ready to tackle
                your project.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Auth form */}
        <div className="flex items-center justify-center px-6 py-16 md:px-12">
          <div className="w-full max-w-md animate-fade-up">
            {/* Logo — frameless, blends with the white background */}
            <img
              src={LOGO_URL}
              alt="Connect A Pro"
              className="mx-auto mb-6 h-48 w-auto object-contain sm:h-56"
              data-testid="auth-form-logo"
            />

            <p className="text-center text-sm text-[#5C605E]">
              {preselectedRole === "client"
                ? "Signing in as a Client to post a job."
                : preselectedRole === "contractor"
                ? "Signing in as a Contractor to find work."
                : "Log in to your account or create a new one below."}
            </p>

            {/* Continue with Google — Emergent-managed OAuth */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                // Forward the preselected role (client/contractor) through the callback so we can apply it after Google auth completes.
                const callbackPath =
                  "/auth/callback" + (preselectedRole ? `?role=${preselectedRole}` : "");
                const redirectUrl = window.location.origin + callbackPath;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
                  redirectUrl
                )}`;
              }}
              data-testid="google-signin-button"
              className="mt-6 flex w-full items-center justify-center gap-3 rounded-md border border-[#E5E2DB] bg-white px-4 py-3 text-sm font-semibold text-[#0A0F0D] transition-all hover:border-[#0A0F0D] hover:shadow-brutal"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#E5E2DB]" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#5C605E]">
                Or with email
              </span>
              <div className="h-px flex-1 bg-[#E5E2DB]" />
            </div>

            <Tabs defaultValue="login">
              <TabsList className="grid w-full grid-cols-2 rounded-md border border-[#E5E2DB] bg-white p-1">
                <TabsTrigger
                  value="login"
                  data-testid="auth-tab-login"
                  className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
                >
                  Log in
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  data-testid="auth-tab-register"
                  className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-[#0A0F0D]">
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      required
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="login-email-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-[#0A0F0D]">
                      Password
                    </Label>
                    <Input
                      id="login-password"
                      type="password"
                      required
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="login-password-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit-button"
                    className="w-full bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                  >
                    {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name" className="text-[#0A0F0D]">
                      Full name
                    </Label>
                    <Input
                      id="reg-name"
                      required
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder="Jane Doe"
                      data-testid="register-name-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-[#0A0F0D]">
                      Email
                    </Label>
                    <Input
                      id="reg-email"
                      type="email"
                      required
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="register-email-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email-confirm" className="text-[#0A0F0D]">
                      Confirm email
                    </Label>
                    <Input
                      id="reg-email-confirm"
                      type="email"
                      required
                      value={regEmailConfirm}
                      onChange={(e) => setRegEmailConfirm(e.target.value)}
                      onPaste={(e) => e.preventDefault()}
                      placeholder="Re-enter your email"
                      data-testid="register-email-confirm-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                    {regEmailConfirm &&
                      regEmail.trim().toLowerCase() !== regEmailConfirm.trim().toLowerCase() && (
                        <p
                          className="text-xs text-[#C65D47]"
                          data-testid="register-email-mismatch-error"
                        >
                          Emails do not match.
                        </p>
                      )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-[#0A0F0D]">
                      Password
                    </Label>
                    <Input
                      id="reg-password"
                      type="password"
                      required
                      minLength={8}
                      pattern="(?=.*[A-Za-z])(?=.*\d).{8,}"
                      title="At least 8 characters, including a letter and a number"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="Min 8 chars, letters & numbers"
                      data-testid="register-password-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                    <p className="text-[11px] text-[#5C605E]" data-testid="register-password-hint">
                      Must be at least 8 characters and include a letter and a number.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-referrer" className="text-[#0A0F0D]">
                      Referral code <span className="text-[#5C605E]">(optional)</span>
                    </Label>
                    <Input
                      id="reg-referrer"
                      value={regReferrer}
                      onChange={(e) => setRegReferrer(e.target.value)}
                      placeholder="Paste your friend's code"
                      data-testid="register-referrer-input"
                      className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="register-submit-button"
                    className="w-full bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                  >
                    {submitting ? "Creating account…" : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <p className="mt-8 text-xs text-[#5C605E]">
              By continuing you agree to our fair-use terms. No credit card required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
